import React from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { shouldShowPhone, FIRMA_TELEFON } from '@/config/kontakt';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  keywords?: string;
  author?: string;
  structuredData?: any;
  noIndex?: boolean;
  alternateLanguages?: { hreflang: string; href: string }[];
}

interface PropertySEOProps {
  property: {
    id: string;
    title: string;
    price: number;
    bedrooms: number;
    bathrooms: number;
    area: number;
    address: string;
    city: string;
    postalCode: string;
    images: string[];
    description: string;
  };
}

export const SEOHead: React.FC<SEOProps> = ({
  title = 'Projektentwickler & Immobilienmakler in Köln | YADE HOME',
  description = 'YADE Home plant, baut und verkauft Wohnimmobilien. Projektentwickler und Immobilienmakler mit Büro in Köln, tätig in Rösrath, Frechen, Remscheid und ganz NRW.',
  canonical,
  ogImage = '/assets/yade-home-og-image.jpg',
  ogType = 'website',
  keywords = 'Projektentwickler Köln, Bauträger Köln, Immobilienmakler Köln, Neubauprojekte Köln, Neubau Köln, Immobilie verkaufen Köln, Immobilienbewertung Köln, Immobilienmakler Remscheid, Immobilienmakler Frechen, Immobilienmakler Rösrath',
  author = 'YADE HOME Real Estate',
  structuredData,
  noIndex = false,
  alternateLanguages = []
}) => {
  // Fallback-Canonical immer auf der Hauptdomain, ohne www, Query und Hash
  const currentUrl =
    typeof window !== 'undefined'
      ? `https://yade-home.de${window.location.pathname === '/' ? '/' : window.location.pathname.replace(/\/$/, '')}`
      : '';
  const canonicalUrl = canonical || currentUrl;
  // og:image/twitter:image brauchen eine absolute URL
  const ogImageUrl = /^https?:\/\//i.test(ogImage) ? ogImage : `https://yade-home.de${ogImage.startsWith('/') ? '' : '/'}${ogImage}`;

  // Enhanced Schema.org with WebSite, LocalBusiness, and RealEstateAgent
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "YADE HOME",
    "url": "https://yade-home.de",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://yade-home.de/immobilien?search={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "YADE HOME",
    "description": "Projektentwickler und Immobilienmakler in Köln",
    "url": "https://yade-home.de",
    "logo": "https://yade-home.de/assets/YadeTransparent.png",
    "image": "https://yade-home.de/assets/yade-home-og-image.jpg",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Charles-de-Gaulle-Platz 1d",
      "addressLocality": "Köln",
      "postalCode": "50679",
      "addressRegion": "Nordrhein-Westfalen",
      "addressCountry": "DE"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 50.9412,
      "longitude": 6.9740
    },
    ...(shouldShowPhone() && {
      "telephone": FIRMA_TELEFON.replace(/\s/g, ''),
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": FIRMA_TELEFON.replace(/\s/g, ''),
        "contactType": "customer service",
        "availableLanguage": ["German", "English"],
        "areaServed": "DE"
      }
    }),
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "10:00",
        "closes": "14:00"
      }
    ],
    "priceRange": "€€€€",
    "areaServed": [
      {
        "@type": "City",
        "name": "Köln"
      },
      {
        "@type": "State",
        "name": "Nordrhein-Westfalen"
      }
    ]
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": "YADE HOME",
    "description": "Projektentwickler und Immobilienmakler in Köln",
    "url": "https://yade-home.de",
    "logo": "https://yade-home.de/assets/YadeTransparent.png",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Köln",
      "addressRegion": "Nordrhein-Westfalen",
      "addressCountry": "DE"
    },
    ...(shouldShowPhone() && {
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": FIRMA_TELEFON.replace(/\s/g, ''),
        "contactType": "customer service",
        "availableLanguage": ["German", "English"]
      }
    }),
    "areaServed": [
      {
        "@type": "City",
        "name": "Köln"
      },
      {
        "@type": "State",
        "name": "Nordrhein-Westfalen"
      }
    ],
    "serviceType": [
      "Real Estate Development",
      "Real Estate Sales",
      "Property Investment Consulting",
      "Real Estate Valuation"
    ]
  };

  // Use provided structured data or default to enhanced schema array
  const finalStructuredData = structuredData || [websiteSchema, localBusinessSchema, organizationSchema];

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      
      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Robots Meta */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="YADE HOME" />
      <meta property="og:locale" content="de_DE" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />
      
      {/* Alternate Languages */}
      {alternateLanguages.map(lang => (
        <link key={lang.hreflang} rel="alternate" hrefLang={lang.hreflang} href={lang.href} />
      ))}
      
      {/* Viewport and Mobile Optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Performance Hints */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//fonts.gstatic.com" />
      <link rel="preconnect" href="https://fonts.googleapis.com" crossOrigin="" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      
      {/* Structured Data */}
      {finalStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(finalStructuredData) ? finalStructuredData : [finalStructuredData])}
        </script>
      )}
      
      {/* Favicon */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      
      {/* Theme Color */}
      <meta name="theme-color" content="#000000" />
      <meta name="msapplication-TileColor" content="#000000" />
    </Helmet>
  );
};

// Specialized SEO component for property pages
export const PropertySEO: React.FC<PropertySEOProps> = ({ property }) => {
  const title = `${property.title} - ${property.area}m² in ${property.city} - YADE HOME`;
  const description = `${property.description.substring(0, 140)}... ✓ ${property.bedrooms} Zimmer ✓ ${property.area}m² ✓ ${property.city} ✓ €${property.price.toLocaleString()}`;
  const canonical = `https://yade-home.de/immobilie/${property.id}`;
  
  const propertySchema = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    "name": property.title,
    "description": property.description,
    "url": canonical,
    "price": {
      "@type": "MonetaryAmount",
      "value": property.price,
      "currency": "EUR"
    },
    "priceCurrency": "EUR",
    "availabilityEnds": new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(), // 90 days from now
    "availabilityStarts": new Date().toISOString(),
    "itemOffered": {
      "@type": "Accommodation",
      "name": property.title,
      "description": property.description,
      "numberOfRooms": property.bedrooms,
      "numberOfBathroomsTotal": property.bathrooms,
      "floorSize": {
        "@type": "QuantitativeValue",
        "value": property.area,
        "unitCode": "MTK"
      },
      "address": {
        "@type": "PostalAddress",
        "streetAddress": property.address,
        "addressLocality": property.city,
        "postalCode": property.postalCode,
        "addressCountry": "DE"
      },
      "image": property.images,
      "amenityFeature": [
        {
          "@type": "LocationFeatureSpecification",
          "name": "Hochwertige Ausstattung"
        }
      ]
    },
    "seller": {
      "@type": "RealEstateAgent",
      "name": "YADE HOME",
      "url": "https://yade-home.de",
      ...(shouldShowPhone() && {
        "telephone": FIRMA_TELEFON.replace(/\s/g, '')
      })
    }
  };

  return (
    <SEOHead
      title={title}
      description={description}
      canonical={canonical}
      ogImage={property.images[0] || '/assets/yade-home-og-image.jpg'}
      ogType="product"
      keywords={`${property.title}, Immobilie ${property.city}, Wohnung kaufen ${property.city}, ${property.bedrooms} Zimmer, ${property.area}m²`}
      structuredData={propertySchema}
    />
  );
};

// SEO Provider component
export const SEOProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <HelmetProvider>
      {children}
    </HelmetProvider>
  );
};

// Hook for dynamic SEO updates
export const useSEO = (seoProps: SEOProps) => {
  return <SEOHead {...seoProps} />;
};

export default SEOHead;
